import React, { useState } from 'react';
import { Lock, Key, ArrowRight, ShieldAlert } from 'lucide-react';
import { setAuthToken } from '../../services/storage.ts';

interface AuthLockModalProps {
  isOpen: boolean;
  onAuthenticated: () => void;
  errorMessage?: string;
}

export const AuthLockModal: React.FC<AuthLockModalProps> = ({
  isOpen,
  onAuthenticated,
  errorMessage,
}) => {
  const [inputKey, setInputKey] = useState('');
  const [error, setError] = useState(errorMessage || '');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let trimmed = inputKey.trim();
    if (!trimmed) {
      setError('Please enter a valid access key or link.');
      return;
    }

    // Support pasting full secret link e.g. http://domain.com/?key=xyz or http://domain.com/?token=xyz
    try {
      if (trimmed.includes('://') || trimmed.includes('?')) {
        const parsed = new URL(trimmed.startsWith('http') ? trimmed : `http://${trimmed}`);
        const tokenFromUrl = parsed.searchParams.get('key') || parsed.searchParams.get('token');
        if (tokenFromUrl) {
          trimmed = tokenFromUrl;
        }
      }
    } catch (_err) {
      // Ignore URL parsing errors and use raw trimmed input
    }

    setAuthToken(trimmed);
    setError('');
    onAuthenticated();
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 9999, backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(8px)' }}>
      <div
        className="modal-content auth-lock-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: '440px', width: '90%', padding: '28px', borderRadius: '16px', background: 'var(--bg-card, #1e1e2d)' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(99, 102, 241, 0.15)',
              color: '#6366f1',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              border: '1px solid rgba(99, 102, 241, 0.3)',
            }}
          >
            <Lock size={32} strokeWidth={2} />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px 0', color: 'var(--text-primary, #fff)' }}>
            Protected Planner
          </h2>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #94a3b8)', margin: 0 }}>
            This ESN O-Weeks workspace requires a secret access key or magic link to enter.
          </p>
        </div>

        {error && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 14px',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '10px',
              color: '#ef4444',
              fontSize: '0.85rem',
              marginBottom: '20px',
            }}
          >
            <ShieldAlert size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="access-key-input"
              style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-secondary, #94a3b8)',
                marginBottom: '8px',
              }}
            >
              Access Key or Shared Link
            </label>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary, #94a3b8)',
                  display: 'flex',
                }}
              >
                <Key size={18} />
              </div>
              <input
                id="access-key-input"
                type="text"
                value={inputKey}
                onChange={(e) => setInputKey(e.target.value)}
                placeholder="Paste key or magic link here..."
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 14px 12px 42px',
                  background: 'var(--bg-input, #0f172a)',
                  border: '1px solid var(--border-color, #334155)',
                  borderRadius: '10px',
                  color: 'var(--text-primary, #fff)',
                  fontSize: '0.95rem',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontWeight: 600,
              fontSize: '0.95rem',
              borderRadius: '10px',
            }}
          >
            <span>Unlock Workspace</span>
            <ArrowRight size={18} />
          </button>
        </form>

        <p style={{ fontSize: '0.78rem', textAlign: 'center', color: 'var(--text-tertiary, #64748b)', marginTop: '20px', marginBottom: 0 }}>
          Ask your ESN O-Weeks coordinator for the shared access key.
        </p>
      </div>
    </div>
  );
};
